import {
  isDesktopNotificationNativeState,
  isDesktopNotificationShowResult,
  type DesktopNotificationItem,
  type DesktopNotificationNativeState,
  type DesktopNotificationShowResult,
} from './desktop-notification-contract';

/** Which notification surface this page has. */
export type DesktopNotificationAvailability =
  | 'browser'
  // Desktop shell built before the notification capability existed.
  | 'desktop_outdated'
  | 'desktop';

export type DesktopNotificationBridge = {
  getPermissionState(): Promise<DesktopNotificationNativeState>;
  show(item: DesktopNotificationItem): Promise<DesktopNotificationShowResult>;
  setBadge(count: number): Promise<boolean>;
  openSettings(): Promise<boolean>;
  subscribeOpen(listener: (deepLink: string) => void): () => void;
};

function readNotifications(shell: unknown): Record<string, unknown> | null {
  if (
    !shell ||
    typeof shell !== 'object' ||
    !('version' in shell) ||
    shell.version !== 1 ||
    !('notifications' in shell)
  )
    return null;
  const value = shell.notifications;
  if (
    !value ||
    typeof value !== 'object' ||
    !('version' in value) ||
    value.version !== 1 ||
    !('supported' in value) ||
    value.supported !== true
  )
    return null;
  const methods = [
    'getPermissionState',
    'show',
    'setBadge',
    'openSettings',
    'subscribeOpen',
  ];
  const record = value as Record<string, unknown>;
  return methods.every((name) => typeof record[name] === 'function')
    ? record
    : null;
}

export function desktopNotificationAvailability(
  shell: unknown,
  hasShell: boolean
): DesktopNotificationAvailability {
  if (!hasShell) return 'browser';
  return readNotifications(shell) ? 'desktop' : 'desktop_outdated';
}

/** Wraps the optional bridge so callers only see bounded, typed results. */
export function readDesktopNotificationBridge(
  shell: unknown
): DesktopNotificationBridge | null {
  const bridge = readNotifications(shell);
  if (!bridge) return null;
  const call = (name: string, ...args: unknown[]): Promise<unknown> =>
    Promise.resolve(
      (bridge[name] as (...values: unknown[]) => unknown)(...args)
    );
  return {
    getPermissionState: async () => {
      const state = await call('getPermissionState');
      return isDesktopNotificationNativeState(state) ? state : 'unsupported';
    },
    show: async (item) => {
      const result = await call('show', item);
      return isDesktopNotificationShowResult(result) ? result : 'unavailable';
    },
    setBadge: async (count) => (await call('setBadge', count)) === true,
    openSettings: async () => (await call('openSettings')) === true,
    subscribeOpen: (listener) => {
      const unsubscribe: unknown = (
        bridge.subscribeOpen as (value: unknown) => unknown
      )(listener);
      return () => {
        if (typeof unsubscribe === 'function') unsubscribe();
      };
    },
  };
}

export function currentDesktopNotificationAvailability(): DesktopNotificationAvailability {
  if (typeof window === 'undefined') return 'browser';
  return desktopNotificationAvailability(
    'imaiDesktop' in window ? window.imaiDesktop : null,
    'imaiDesktop' in window
  );
}

export function currentDesktopNotificationBridge(): DesktopNotificationBridge | null {
  if (typeof window === 'undefined' || !('imaiDesktop' in window)) return null;
  return readDesktopNotificationBridge(window.imaiDesktop);
}
